/**
 * Shared date/time formatting — respects company regional settings
 * (Date Format, 24-Hour Clock).
 *
 * Pure utilities + a React hook that binds company regional settings
 * (`useCompanyInfo()` — `dateFormat`, `use24HourClock`) to ready-to-call
 * formatters. Zero runtime deps — uses the native `Date` object and
 * hand-built formatters for deterministic output that matches the
 * three canonical `dateFormat` tokens.
 *
 * Input is forgiving:
 *   - ISO date strings (`"2026-04-24"`, `"2026-04-24T14:30:00Z"`)
 *   - Time-only strings (`"14:30"`, `"14:30:00"`)
 *   - `Date` instances
 *   - `null` / `undefined` / invalid → `''`
 *
 * Usage:
 *
 *   import { useDateTimeFormatters } from '../../lib/datetime-format';
 *   const { formatDate, formatTime, formatDateTime } = useDateTimeFormatters();
 *   <td>{formatDate(invoice.createdAt)}</td>
 *
 * Or framework-free:
 *   import { formatDateValue } from '../../lib/datetime-format';
 *   formatDateValue('2026-04-24', 'MM/DD/YYYY') // → '04/24/2026'
 */

import { useMemo } from 'react';
import { useCompanyInfo } from '../hooks/use-settings';

export type DateFormatToken = 'MM/DD/YYYY' | 'DD/MM/YYYY' | 'YYYY-MM-DD';

// ---------------------------------------------------------------------------
// Internals
// ---------------------------------------------------------------------------

const pad2 = (n: number): string => String(n).padStart(2, '0');

/**
 * Pull `HH:mm(:ss)?` out of a time-only string. Returns `null` if the
 * string doesn't cleanly match. Ignores trailing garbage / timezone
 * fragments for robustness.
 */
function parseTimeOnly(input: string): { hours: number; minutes: number } | null {
  const m = /^(\d{1,2}):(\d{2})(?::(\d{2}))?/.exec(input.trim());
  if (!m) return null;
  const hours = Number(m[1]);
  const minutes = Number(m[2]);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;
  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return null;
  return { hours, minutes };
}

/**
 * Coerce arbitrary input to a `Date` or `null`. Rejects invalid dates
 * (`new Date('foo')` → `null`). Does NOT handle time-only strings — callers
 * that need time-only support check `parseTimeOnly` first.
 *
 * Hard-rejects non-string / non-Date inputs up front. A CDT cell can contain
 * a number, object, boolean, etc. if the CDT was retyped from (say) `number`
 * to `datetime` after records already existed. Without this guard,
 * `new Date(0)` fabricates `1970-01-01T00:00:00Z` and the grid shows
 * `"01/01/1970"` for what the user perceives as an empty cell.
 *
 * Also rejects whitespace-only strings and the common legacy MySQL
 * zero-date sentinel (`"0000-00-00 00:00:00"` / `"0000-00-00"`).
 */
function toDate(value: unknown): Date | null {
  if (value == null) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (trimmed === '') return null;
  // Legacy MySQL zero-date sentinels — various parsers return either
  // Invalid Date or a meaningless year-0 / year-2000 value.
  if (/^0000-00-00(?:[ T]00:00:00)?$/.test(trimmed)) return null;
  const d = new Date(trimmed);
  if (Number.isNaN(d.getTime())) return null;
  return d;
}

// ---------------------------------------------------------------------------
// Public utilities
// ---------------------------------------------------------------------------

/**
 * Formats an ISO date string (or Date, or null) per the company's Date Format
 * regional setting. Returns empty string for null / invalid input.
 */
export function formatDateValue(
  value: string | Date | null | undefined,
  dateFormat: DateFormatToken,
): string {
  const d = toDate(value);
  if (!d) return '';

  // Pull calendar parts using local time. Inputs like "2026-04-24"
  // (bare YYYY-MM-DD) are parsed as UTC midnight by the JS engine; using
  // local getters then shifts them one day back in negative UTC offsets.
  // To make bare date strings stable, detect and parse them manually.
  let year: number;
  let month: number;
  let day: number;

  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    // Bare calendar date — treat literally, no timezone shifting.
    const [y, m, dd] = value.split('-').map(Number);
    year = y;
    month = m;
    day = dd;
  } else {
    year = d.getFullYear();
    month = d.getMonth() + 1;
    day = d.getDate();
  }

  const MM = pad2(month);
  const DD = pad2(day);
  const YYYY = String(year);

  switch (dateFormat) {
    case 'DD/MM/YYYY':
      return `${DD}/${MM}/${YYYY}`;
    case 'YYYY-MM-DD':
      return `${YYYY}-${MM}-${DD}`;
    case 'MM/DD/YYYY':
    default:
      return `${MM}/${DD}/${YYYY}`;
  }
}

/**
 * Wall-clock DATE formatter (date-only companion to
 * `formatWallClockDateTimeValue`).
 *
 * Use for date-only fields whose stored value is a wall-clock-as-fake-UTC
 * timestamp — notably `Load.pickupDate` / `Load.deliveryDate` (Ship Date /
 * Delivery Date), which `deriveLoadDates` persists as the MIN/MAX of the
 * stops' appointment times. The operator-entered calendar day lives in the
 * value's `YYYY-MM-DD` prefix (or its UTC fields for a Date instance), NOT in
 * the browser's local zone.
 *
 * `formatDateValue` reads LOCAL calendar parts (`getFullYear/getMonth/getDate`)
 * for any value carrying a time component, so a `"2026-06-30T00:00:00.000Z"`
 * appointment renders as `06/29` in negative-UTC browsers (US Central, etc.).
 * This helper extracts the calendar day verbatim — no timezone math — so the
 * grid always matches the date the operator sees in the load form.
 */
export function formatWallClockDateValue(
  value: string | Date | null | undefined,
  dateFormat: DateFormatToken,
): string {
  if (value == null || value === '') return '';
  if (typeof value === 'string') {
    // Pull the calendar-date prefix verbatim (bare date OR ISO with time,
    // any zone suffix). Feed the bare `YYYY-MM-DD` back through
    // formatDateValue's literal (no-shift) branch.
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value.trim());
    if (m) return formatDateValue(`${m[1]}-${m[2]}-${m[3]}`, dateFormat);
  } else if (value instanceof Date && !Number.isNaN(value.getTime())) {
    // Date instance — the wall-clock day is in the UTC fields (fake-UTC
    // storage convention). Read UTC parts to avoid the local-zone shift.
    const iso = `${String(value.getUTCFullYear()).padStart(4, '0')}-${pad2(
      value.getUTCMonth() + 1,
    )}-${pad2(value.getUTCDate())}`;
    return formatDateValue(iso, dateFormat);
  }
  // Non-ISO string / unexpected shape — defer to the standard formatter.
  return formatDateValue(value, dateFormat);
}

/**
 * Formats an ISO time or datetime string to a time string per the 24-hour
 * clock preference. For `use24Hour=true` returns `"HH:mm"`. For `false`
 * returns `"h:mm A"`. Returns empty string for null / invalid.
 *
 * Accepts time-only strings (`"14:30"`, `"14:30:00"`) as well as full
 * datetime values.
 */
export function formatTimeValue(
  value: string | Date | null | undefined,
  use24Hour: boolean,
): string {
  if (value == null || value === '') return '';

  let hours: number;
  let minutes: number;

  if (typeof value === 'string') {
    // Time-only string? (no `T`, no date portion)
    const isTimeOnly = !value.includes('T') && !/^\d{4}-\d{2}-\d{2}/.test(value);
    if (isTimeOnly) {
      const parsed = parseTimeOnly(value);
      if (!parsed) return '';
      hours = parsed.hours;
      minutes = parsed.minutes;
      return renderTime(hours, minutes, use24Hour);
    }
  }

  const d = toDate(value);
  if (!d) return '';
  hours = d.getHours();
  minutes = d.getMinutes();
  return renderTime(hours, minutes, use24Hour);
}

function renderTime(hours: number, minutes: number, use24Hour: boolean): string {
  if (use24Hour) {
    return `${pad2(hours)}:${pad2(minutes)}`;
  }
  const period = hours >= 12 ? 'PM' : 'AM';
  const h12 = hours % 12 === 0 ? 12 : hours % 12;
  return `${h12}:${pad2(minutes)} ${period}`;
}

/**
 * Combines date + time with a space separator, each formatted per the
 * respective rule. Returns empty string for null / invalid.
 */
export function formatDateTimeValue(
  value: string | Date | null | undefined,
  dateFormat: DateFormatToken,
  use24Hour: boolean,
): string {
  const d = toDate(value);
  if (!d) return '';
  const datePart = formatDateValue(d, dateFormat);
  const timePart = renderTime(d.getHours(), d.getMinutes(), use24Hour);
  if (!datePart) return '';
  return `${datePart} ${timePart}`;
}

/**
 * WAYP-814 follow-up (May 2026) — wall-clock datetime formatter.
 *
 * Companion to `formatDateTimeValue` for fields that carry V1-parity
 * "wall-clock time" semantics. Use this for appointment / scheduled-
 * stop timestamps where the value represents "5:00 AM at the stop's
 * location", not "5:00 AM UTC". The picker
 * (`DateTimePicker::dateToIsoDateTimeLocal`) and the send-side helper
 * (`QuoteWizard::toIsoOrUndefined`) preserve the wall-clock by
 * appending `:00.000Z` without applying a timezone shift; this
 * formatter is the symmetric read path.
 *
 * Why a separate helper instead of fixing `formatDateTimeValue`:
 * `formatDateTimeValue` runs the value through `new Date(value)` +
 * `.getHours()`, which means a `"…T01:00:00.000Z"` input renders as
 * `06:30` in IST (UTC+5:30) — that's correct for moment-in-time
 * fields (`createdAt`, login timestamps), wrong for appointment
 * times (operator typed `01:00`, sees `06:30` on reload).
 *
 * Algorithm: regex-extract the wall-clock prefix
 * (`YYYY-MM-DDTHH:mm`) and render the calendar parts directly. If
 * the input doesn't match the wall-clock shape (legacy moment-in-
 * time values, Date instances), fall back to the standard formatter.
 *
 * Accepts the same input shapes as `formatDateTimeValue` so callers
 * can swap with no other change.
 */
export function formatWallClockDateTimeValue(
  value: string | Date | null | undefined,
  dateFormat: DateFormatToken,
  use24Hour: boolean,
): string {
  if (value == null || value === '') return '';
  if (typeof value === 'string') {
    // Pull the wall-clock prefix verbatim — works for the picker's
    // bare `"YYYY-MM-DDTHH:mm"` AND for any timezone-suffixed
    // variant the wire might carry (`Z`, `+05:30`, etc.). All we
    // care about are the calendar parts the operator typed.
    const m =
      /^(\d{4})-(\d{2})-(\d{2})[T ](\d{1,2}):(\d{2})/.exec(value.trim());
    if (m) {
      const y = m[1];
      const mo = m[2];
      const d = m[3];
      const h = Number(m[4]);
      const mi = Number(m[5]);
      const isoDate = `${y}-${mo}-${d}`;
      const datePart = formatDateValue(isoDate, dateFormat);
      const timePart = renderTime(h, mi, use24Hour);
      if (!datePart) return '';
      return `${datePart} ${timePart}`;
    }
  }
  // Fallback for Date instances or non-ISO strings — defer to the
  // standard formatter (applies local-timezone conversion).
  return formatDateTimeValue(value, dateFormat, use24Hour);
}

/**
 * SGP polish (May 2026) — moment-in-time formatter that renders in a
 * SPECIFIC IANA timezone instead of the browser's local zone.
 *
 * Use this for true UTC instants (`changedAt`, audit/recorded-at
 * columns) that should display in the company's configured timezone
 * (`CompanyInfo.timezone`) regardless of where the operator's browser
 * is. The timestamp-history modal renders each row's `changedAt` —
 * "when the edit was recorded" — through this so a dispatcher in IST
 * and one in EST both see the change stamped in the org's timezone.
 *
 * Contrast with:
 *   - `formatDateTimeValue` → renders in the browser's local zone.
 *   - `formatWallClockDateTimeValue` → renders the typed calendar parts
 *     verbatim (no zone math) for appointment / wall-clock fields.
 *
 * Implementation uses `Intl.DateTimeFormat` with the `timeZone` option
 * (DST-aware, zero deps). Falls back to local-zone formatting when no
 * timezone is configured or the string is rejected by the engine.
 */
export function formatDateTimeValueInZone(
  value: string | Date | null | undefined,
  dateFormat: DateFormatToken,
  use24Hour: boolean,
  timeZone: string | null | undefined,
): string {
  const d = toDate(value);
  if (!d) return '';
  // No company timezone configured → behave like the standard formatter.
  if (!timeZone) return formatDateTimeValue(d, dateFormat, use24Hour);

  let parts: Intl.DateTimeFormatPart[];
  try {
    parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).formatToParts(d);
  } catch {
    // Invalid / unknown timeZone string — don't blow up the modal; fall
    // back to local-zone rendering.
    return formatDateTimeValue(d, dateFormat, use24Hour);
  }

  const pick = (type: Intl.DateTimeFormatPartTypes): string =>
    parts.find((p) => p.type === type)?.value ?? '';

  const year = Number(pick('year'));
  const month = Number(pick('month'));
  const day = Number(pick('day'));
  let hour = Number(pick('hour'));
  const minute = Number(pick('minute'));
  if (![year, month, day, hour, minute].every(Number.isFinite)) {
    return formatDateTimeValue(d, dateFormat, use24Hour);
  }
  // Some engines emit hour '24' for midnight under hour12:false.
  if (hour === 24) hour = 0;

  // Feed a bare YYYY-MM-DD string to formatDateValue — its literal
  // (no-shift) branch keeps the already-zone-adjusted parts intact.
  const isoDate = `${String(year).padStart(4, '0')}-${pad2(month)}-${pad2(day)}`;
  const datePart = formatDateValue(isoDate, dateFormat);
  const timePart = renderTime(hour, minute, use24Hour);
  if (!datePart) return '';
  return `${datePart} ${timePart}`;
}

/**
 * ATL-000661/7 — "today" as the ORG sees it, not as the browser sees it.
 *
 * Zara asked whether the planner's calendar/rolling surfaces run off the
 * browser's date. The Weekly Rolling window doesn't — the server hands it an
 * org-timezone `today` anchor — but any surface that calls `new Date()` for
 * "today" is on the operator's own clock, so a dispatcher in IST working a
 * US-Central org sees the next business day highlighted from ~10:30 IST onward.
 *
 * Returns a BROWSER-LOCAL midnight Date carrying the org timezone's current
 * calendar Y/M/D. Local-midnight (not a UTC instant) on purpose: every calendar
 * grid, `isSameDay` and week-anchor helper around it does plain local getters,
 * and handing them a shifted instant would break placement instead of fixing it.
 * Falls back to browser-local today when no org timezone is configured or the
 * engine rejects the zone.
 */
export function startOfTodayInZone(timeZone: string | null | undefined): Date {
  const localMidnight = (): Date => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  };
  if (!timeZone) return localMidnight();
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(new Date());
    const num = (type: string): number =>
      Number(parts.find((p) => p.type === type)?.value ?? NaN);
    const year = num('year');
    const month = num('month');
    const day = num('day');
    if (![year, month, day].every(Number.isFinite)) return localMidnight();
    return new Date(year, month - 1, day);
  } catch {
    // Unknown / invalid IANA zone — never let a preference blank the calendar.
    return localMidnight();
  }
}

// ---------------------------------------------------------------------------
// Quick-search parse helpers (WAYP-561 pass 6)
// ---------------------------------------------------------------------------
//
// The "Search all columns…" input above the Locations grid sends `?search=`
// (free-text contains across the text columns). For CDT date/time/datetime
// junctions, that contains-against-text doesn't match — junctions store
// typed values (`valueDate`, `valueTime`). To bridge, the page parses the
// typed input here against the company's `dateFormat` / 24-hour preference
// and ships *additional* `searchDate` / `searchTime` / `searchDateTime`
// query params alongside the original `search`. The backend ORs them into
// the search OR clause so the user sees both text matches AND typed-value
// matches.
//
// All three helpers return `null` when the input doesn't match — that's
// the sentinel the page uses to decide whether to ship the extra param at
// all. Liberal in what we accept (whitespace, optional leading zeros) but
// strict on shape (the format must be unambiguous in the company's locale,
// otherwise we don't ship the typed param and the user falls back to plain
// text search).

const _RE_DATE_MMDDYYYY = /^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/;
const _RE_DATE_DDMMYYYY = /^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/;
const _RE_DATE_YYYYMMDD = /^(\d{4})[/-](\d{1,2})[/-](\d{1,2})$/;
const _RE_TIME_24H = /^(\d{1,2}):(\d{2})$/;
const _RE_TIME_12H = /^(\d{1,2}):(\d{2})\s*(AM|PM)$/i;

function _validDateParts(y: number, m: number, d: number): boolean {
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return false;
  if (y < 1000 || y > 9999) return false;
  if (m < 1 || m > 12) return false;
  if (d < 1 || d > 31) return false;
  // Calendar-aware bounds — guard 02/30, 04/31, etc. JS Date overflow on
  // invalid days yields a different month, so reverse-check via Date.
  const probe = new Date(y, m - 1, d);
  return (
    probe.getFullYear() === y &&
    probe.getMonth() === m - 1 &&
    probe.getDate() === d
  );
}

function _pad2(n: number): string {
  return String(n).padStart(2, '0');
}

/**
 * Parse a date string typed in the company's `dateFormat`. Returns the
 * canonical ISO `YYYY-MM-DD` form, or `null` when the input doesn't match
 * the format unambiguously.
 *
 * Accepts both `/` and `-` separators ergonomically (the user might type
 * either). Year is always 4 digits — `04/26/26` is rejected since the
 * 2-digit-year ambiguity isn't worth the complexity for a quick-search.
 */
export function parseCompanyDate(
  input: string | null | undefined,
  dateFormat: DateFormatToken,
): string | null {
  if (input == null) return null;
  const s = input.trim();
  if (s === '') return null;

  let y = NaN;
  let m = NaN;
  let d = NaN;

  if (dateFormat === 'YYYY-MM-DD') {
    const match = _RE_DATE_YYYYMMDD.exec(s);
    if (!match) return null;
    y = Number(match[1]);
    m = Number(match[2]);
    d = Number(match[3]);
  } else if (dateFormat === 'DD/MM/YYYY') {
    const match = _RE_DATE_DDMMYYYY.exec(s);
    if (!match) return null;
    d = Number(match[1]);
    m = Number(match[2]);
    y = Number(match[3]);
  } else {
    const match = _RE_DATE_MMDDYYYY.exec(s);
    if (!match) return null;
    m = Number(match[1]);
    d = Number(match[2]);
    y = Number(match[3]);
  }

  if (!_validDateParts(y, m, d)) return null;
  return `${String(y).padStart(4, '0')}-${_pad2(m)}-${_pad2(d)}`;
}

/**
 * Parse a time string in either 12-hour (`5:30 PM`) or 24-hour (`17:30`)
 * shape and return canonical `HH:mm` 24-hour form. `null` when no match.
 *
 * Both forms are accepted regardless of `use24HourClock` — quick-search is
 * forgiving since the user might type either way. The canonical wire form
 * the server expects on `valueTime` is always `HH:mm` 24-hour.
 */
export function parseCompanyTime(input: string | null | undefined): string | null {
  if (input == null) return null;
  const s = input.trim();
  if (s === '') return null;

  // 12-hour with AM/PM suffix.
  const m12 = _RE_TIME_12H.exec(s);
  if (m12) {
    let hour = Number(m12[1]);
    const min = Number(m12[2]);
    const isPm = m12[3].toUpperCase() === 'PM';
    if (
      !Number.isFinite(hour) ||
      !Number.isFinite(min) ||
      hour < 1 ||
      hour > 12 ||
      min < 0 ||
      min > 59
    ) {
      return null;
    }
    if (isPm && hour < 12) hour += 12;
    if (!isPm && hour === 12) hour = 0;
    return `${_pad2(hour)}:${_pad2(min)}`;
  }

  // 24-hour HH:mm.
  const m24 = _RE_TIME_24H.exec(s);
  if (m24) {
    const hour = Number(m24[1]);
    const min = Number(m24[2]);
    if (
      !Number.isFinite(hour) ||
      !Number.isFinite(min) ||
      hour < 0 ||
      hour > 23 ||
      min < 0 ||
      min > 59
    ) {
      return null;
    }
    return `${_pad2(hour)}:${_pad2(min)}`;
  }

  return null;
}

/**
 * Parse a datetime string typed as `<company date> <time>` (single space
 * separator) and return canonical `YYYY-MM-DDTHH:mm`. `null` when either
 * half doesn't parse.
 *
 * Whitespace between the halves is collapsed: `04/26/2026  1:00 AM` →
 * `2026-04-26T01:00`.
 */
export function parseCompanyDateTime(
  input: string | null | undefined,
  dateFormat: DateFormatToken,
): string | null {
  if (input == null) return null;
  const s = input.trim();
  if (s === '') return null;

  // Split on the first run of whitespace — date portion before, time after.
  const splitIdx = s.search(/\s+/);
  if (splitIdx <= 0) return null;
  const datePart = s.slice(0, splitIdx).trim();
  const timePart = s.slice(splitIdx).trim();
  if (timePart === '') return null;

  const isoDate = parseCompanyDate(datePart, dateFormat);
  if (!isoDate) return null;
  const isoTime = parseCompanyTime(timePart);
  if (!isoTime) return null;

  return `${isoDate}T${isoTime}`;
}

// ---------------------------------------------------------------------------
// React hook
// ---------------------------------------------------------------------------

/**
 * React hook: reads company info once, returns memoized bound formatters
 * for `date`, `time`, and `datetime`. Safe to call from any component
 * that wants to render company-aware date values. Falls back to
 * `MM/DD/YYYY` + 12-hour if company info hasn't loaded yet (or the user
 * is not authenticated).
 */
export function useDateTimeFormatters(): {
  formatDate: (value: string | Date | null | undefined) => string;
  /**
   * ATL-000968 — wall-clock DATE variant of `formatDate`. Use for date-only
   * fields stored as a wall-clock-as-fake-UTC timestamp (e.g.
   * `Settlement.settlementDate`), where the operator-entered calendar day
   * lives in the value's `YYYY-MM-DD` prefix, not the browser's local zone.
   * `formatDate` shifts such values one day back in negative-UTC browsers.
   */
  formatWallClockDate: (value: string | Date | null | undefined) => string;
  formatTime: (value: string | Date | null | undefined) => string;
  formatDateTime: (value: string | Date | null | undefined) => string;
  /**
   * WAYP-814 follow-up (May 2026) — wall-clock variant of
   * `formatDateTime`. Use this for appointment / scheduled-stop
   * timestamps that were entered through the wizard's
   * `DateTimePicker` (preserves the typed calendar parts verbatim,
   * no UTC→local shift). See `formatWallClockDateTimeValue` docstring
   * for the full rationale.
   */
  formatWallClockDateTime: (value: string | Date | null | undefined) => string;
  /**
   * SGP polish (May 2026) — renders a true UTC instant in the company's
   * configured timezone (`CompanyInfo.timezone`) rather than the
   * browser's local zone. Use for recorded-at / audit timestamps
   * (e.g. the timestamp-history modal's `changedAt`). Falls back to
   * local-zone formatting when no company timezone is set.
   */
  formatDateTimeInCompanyZone: (value: string | Date | null | undefined) => string;
  dateFormat: DateFormatToken;
  use24Hour: boolean;
  timeZone: string | null;
} {
  const { data } = useCompanyInfo();
  const dateFormat: DateFormatToken = data?.dateFormat ?? 'MM/DD/YYYY';
  const use24Hour: boolean = data?.use24HourClock ?? false;
  const timeZone: string | null = data?.timezone ?? null;

  return useMemo(
    () => ({
      formatDate: (value) => formatDateValue(value, dateFormat),
      formatWallClockDate: (value) => formatWallClockDateValue(value, dateFormat),
      formatTime: (value) => formatTimeValue(value, use24Hour),
      formatDateTime: (value) => formatDateTimeValue(value, dateFormat, use24Hour),
      formatWallClockDateTime: (value) =>
        formatWallClockDateTimeValue(value, dateFormat, use24Hour),
      formatDateTimeInCompanyZone: (value) =>
        formatDateTimeValueInZone(value, dateFormat, use24Hour, timeZone),
      dateFormat,
      use24Hour,
      timeZone,
    }),
    [dateFormat, use24Hour, timeZone],
  );
}
